import { useState, useEffect, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { Dialog } from "primereact/dialog";
import AppLoading from "../app-loading/app-loading";
import orderServices from "../../services/orders/orderservice";
import ViewChallanDetails from "./View-Challan-Details";
import EwayForm from "./EWayForm";

interface OutwardChallan {
  challanNo: number;
  challanType: string;
  createdDate: string;
  ewbNo: string | null;
  name: string;
  hasEwayBill?: boolean;
}

interface ChallanDetails {
  challanNo: number;
  challanType: string;
  generatedBy: string;
  fromAddress: string;
  toAddress: string;
  lineItems: any[];
  categorySummaries: any[];
  totalCount: number;
  hasEwayBill: boolean;
}

const ViewAllChallanDatatable = () => {
  const toast = useRef<Toast>(null);
  const [challans, setChallans] = useState<OutwardChallan[]>([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState<1 | -1>();
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedChallan, setSelectedChallan] = useState<number | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [editingChallan, setEditingChallan] = useState<number | null>(null);
  const [challanSearch, setChallanSearch] = useState<number | null>(null);
  const [ewayDialog, setEwayDialog] = useState(false);
  const [selectedChallanNo, setSelectedChallanNo] = useState<number | null>(null);

  const formatDate = (date: Date | null | undefined) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const fetchChallans = async () => {
    setLoading(true);
    try {
      const payload = {
        startDate: startDate ? formatDate(startDate) : null,
        endDate: endDate ? formatDate(endDate) : null,
         challanNo: challanSearch || null,
        pageNumber: page,
        pageSize,
        sortField,
        sortOrder: sortOrder === 1 ? "asc" : "desc",
      };

      const result = await orderServices.GetChallanDetails(payload);
      setChallans(result.data);
      setTotalRecords(result.totalCount);
    } catch (err) {
      console.error(err);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch challans.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChallans();
  }, [page, pageSize, sortField, sortOrder]);

  const grossAmountBody = (rowData: any) => {
  const amount = rowData.grossAmount ?? 0;
  const roundedAmount = Number(amount).toFixed(2);
  const isHigh = Number(roundedAmount) >= 50000;

  return (
    <span
      style={{
        backgroundColor: isHigh ? "red" : "#eeeeeeff",
        color: isHigh ? "white" : "black",
        padding: "4px 8px",
        borderRadius: "6px",
        display: "inline-block",
        minWidth: "80px",
        textAlign: "right"
      }}
    >
      ₹ {Number(roundedAmount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
    </span>
  );
};

  const dateBody = (rowData: OutwardChallan) =>
    new Date(rowData.createdDate).toLocaleDateString("en-GB");

  /** ✅ Fetch and show challan details in popup */
  const handleViewChallan = async (rowData: OutwardChallan) => {
    const challanNo = rowData.challanNo;

    try {
      setDetailsLoading(true);  
      setSelectedChallan(challanNo);   
      setViewDialog(true);
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch challan details.",
      });
    } finally {
      setDetailsLoading(false);
    }
  };

  /** ✅ Update local EWB number */
  const handleEwbNoChange = (value: string, challanNo: number) => {
    setChallans(prev =>
      prev.map(c => (c.challanNo === challanNo ? { ...c, ewbNo: value } : c))
    );
  };

  const handleFetchEwayBill = async (rowData: OutwardChallan) => {
    if (!rowData.ewbNo?.trim()) {
      toast.current?.show({
        severity: "warn",
        summary: "Missing EWB No",
        detail: "Please enter a valid E-Way Bill number before fetching.",
      });
      return;
    }

    try {
      setLoading(true);
      const res = await orderServices.FetchEwayBill(rowData.ewbNo, rowData.challanNo);
      if (res?.isSuccess) {
        toast.current?.show({
          severity: "success",
          summary: "Fetched Successfully",
          detail: res.message || "E-Way Bill fetched successfully.",
        });
        setEditingChallan(null);
        fetchChallans();
      } else {
        handleEwbNoChange("", rowData.challanNo);
        setEditingChallan(null);
        toast.current?.show({
          severity: "error",
          summary: "Fetch Failed",
          detail: res?.message || "Failed to fetch E-Way Bill.",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const ewbNoBody = (rowData: OutwardChallan) => {
    const isEditing = editingChallan === rowData.challanNo;

    // ✅ If EWB exists and not editing → show non-editable text
    if (rowData.ewbNo && rowData.ewbNo.trim() !== "" && !isEditing) {
      return <span>{rowData.ewbNo}</span>;
    }

    // ✅ Show input + fetch button when:
    return (
      <div className="flex gap-2 items-center">
        <InputText
          value={rowData.ewbNo ?? ""}
          placeholder="Enter E-Way Bill No"
          className="w-full"
          onChange={(e) => {
            if (!isEditing) setEditingChallan(rowData.challanNo); // start editing
            handleEwbNoChange(e.target.value, rowData.challanNo);
          }}
          autoFocus={isEditing}
        />

        <Button
          icon="pi pi-check"
          severity="success"
          rounded
          outlined
          tooltip="Fetch & Store E-Way Bill"
          onClick={() => handleFetchEwayBill(rowData)}
        />

      </div>
    );
  };

  const handleAddEwayBill = (rowData: OutwardChallan) => {
    setSelectedChallanNo(rowData.challanNo);
    setEwayDialog(true);
  };

  /** ✅ Actions column */
  const actionBodyTemplate = (rowData: OutwardChallan) => (
    <div className="flex gap-2 justify-center">
      <Button
        icon="pi pi-file-pdf"
        className="p-button-rounded p-button-info p-button-text"
        tooltip="View Challan"
        tooltipOptions={{ position: "top" }}
        onClick={() => handleViewChallan(rowData)}
      />

      {!rowData.ewbNo && (
        <Button
          icon="pi pi-plus"
          className="p-button-rounded p-button-success p-button-text"
          tooltip="Add E-Way Bill"
          tooltipOptions={{ position: "top" }}
          onClick={() => handleAddEwayBill(rowData)}
        />
      )}
    </div>
  );

  const handleFilter = () => {
    if ((startDate && !endDate) || (!startDate && endDate)) {
      toast.current?.show({
        severity: "warn",
        summary: "Invalid Date Range",
        detail: "Please select both Start Date and End Date.",
      });
      return;
    }
    setPage(1);
    setPageSize(10); 
    fetchChallans();
  };

  const handleReset = () => {
    setStartDate(null);
    setEndDate(null);
    setPage(1);
    setPageSize(10);
    setChallanSearch(null);
    setChallans([]);
    setTotalRecords(0);
  };

  return (
    <div className="col-12">
      <Toast ref={toast} position="top-right" />
      <h5 className="ml-1 mb-0 pb-3">View Challan</h5>

      {/* === Filter Section === */}
      <div className="card border-noround p-3 w-full mb-3">
        <div className="grid gap-2">
          <div className="col-6 md:col-2">
            <Calendar
              value={startDate}
              onChange={(e) => setStartDate(e.value as Date | null)}
              showButtonBar
              dateFormat="dd/mm/yy"
              placeholder="Start Date"
              className="w-full"
              readOnlyInput
            />
          </div>
          <div className="col-6 md:col-2">
            <Calendar
              value={endDate}
              onChange={(e) => setEndDate(e.value as Date | null)}
              showButtonBar
              dateFormat="dd/mm/yy"
              placeholder="End Date"
              className="w-full"
              readOnlyInput
            />
          </div>
          <div className="col-6 md:col-2">
            <InputText
              value={challanSearch !== null ? challanSearch.toString() : ""}
              onChange={(e) => {
                const value = e.target.value.trim();
                setChallanSearch(value === "" ? null : Number(value));
              }}
              placeholder="Search Challan No"
              className="w-full"
            />
          </div>
          <div className="col-6 md:col-2">
            <Button
              label="Filter"
              severity="success"
              className="w-full"
              onClick={handleFilter}
            />
          </div>
          <div className="col-6 md:col-2">
            <Button
              label="Reset"
              severity="secondary"
              className="w-full"
              onClick={handleReset}
            />
          </div>
        </div>
      </div>

      {/* === Data Table === */}
      <div className="card border-noround p-2 mb-2">
        {loading ? (
          <AppLoading />
        ) : (
          <DataTable
            value={challans}
            paginator
            rows={pageSize}
            first={(page - 1) * pageSize}
            totalRecords={totalRecords}
            rowsPerPageOptions={[10, 25, 50, 100]}
            lazy
            stripedRows
            showGridlines
            sortField={sortField}
            sortOrder={sortOrder}
            paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
            currentPageReportTemplate="{first} to {last} of {totalRecords}"
            onPage={(e) => {
              setPage((e.page ?? 0) + 1);
              setPageSize(e.rows ?? pageSize);
            }}
            onSort={(e) => {
              setSortField(e.sortField);
              setSortOrder(e.sortOrder as 1 | -1);
            }}
          >
            <Column field="challanNo" header="Challan No" sortable />
            <Column field="grossAmount" header="Challan Value (₹)" body={grossAmountBody} sortable />
            <Column field="name" header="Generated By" sortable />
            <Column field="challanType" header="Challan Type" sortable />
            <Column field="createdDate" header="Generated Date" body={dateBody} sortable />
            <Column field="ewbNo" header="E-Way Bill No" body={ewbNoBody} sortable />
            <Column header="Actions" body={actionBodyTemplate} />
          </DataTable>
        )}
      </div>

      {/* === View Challan Dialog === */}
      <Dialog
        header={`Challan Details`}
        visible={viewDialog}
        style={{ width: "70vw" }}
        modal
        onHide={() => setViewDialog(false)}
      >
        {detailsLoading ? (
          <AppLoading />
        ) : selectedChallan ? (
          <ViewChallanDetails challanNo={selectedChallan} />
        ) : (
          <div className="text-center p-4">No challan details found.</div>
        )}
      </Dialog>

      {/* === Add E-Way Bill Dialog === */}
      <Dialog
        header={`Add E-Way Bill for Challan ${selectedChallanNo ?? ""}`}
        visible={ewayDialog}
        style={{ width: "50vw" }}
        modal
        onHide={() => setEwayDialog(false)}
      >
        {selectedChallanNo && (
          <EwayForm
            order={{ challanNo: selectedChallanNo } as any}
            onCancel={() => setEwayDialog(false)}
            onSuccess={() => {
              setEwayDialog(false);
              fetchChallans();
              toast.current?.show({
                severity: "success",
                summary: "Success",
                detail: "E-Way Bill generated successfully.",
              });
            }}
          />
        )}
      </Dialog>

    </div>
  );
};

export default ViewAllChallanDatatable;
